import {
  Sparkles, TrendingUp, AlertTriangle, Target, Crown, Bot, BadgeAlert,
} from 'lucide-react';

const SUBSCORE_LABEL = {
  firm_brand_clarity: 'Firm Brand Clarity',
  practice_area_clarity: 'Practice Area Clarity',
  local_legal_discoverability: 'Local Legal Discoverability',
  ai_citation_readiness: 'AI Citation Readiness',
  conversational_search_readiness: 'Conversational Search Readiness',
  content_depth: 'Content Depth',
  competitor_visibility_strength: 'Competitor Visibility Strength',
};

export function ScoreRing({ score = 0, label, size = 'lg' }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const radius = size === 'lg' ? 56 : 32;
  const stroke = size === 'lg' ? 8 : 6;
  const c = 2 * Math.PI * radius;
  const offset = c - (pct / 100) * c;
  const color = pct < 45 ? '#dc2626' : pct < 65 ? '#f59e0b' : '#10b981';
  return (
    <div className={`relative ${size === 'lg' ? 'w-32 h-32' : 'w-20 h-20'}`}>
      <svg viewBox={`0 0 ${(radius + stroke) * 2} ${(radius + stroke) * 2}`} className="w-full h-full -rotate-90">
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={radius + stroke} cy={radius + stroke} r={radius} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className={`font-bold text-slate-900 ${size === 'lg' ? 'text-3xl' : 'text-lg'}`} style={{ fontFamily: 'Outfit' }}>{pct}</div>
        {label && size === 'lg' && <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>}
      </div>
    </div>
  );
}

export function IntentBadge({ intent }) {
  const map = {
    transactional: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    commercial: 'bg-violet-100 text-violet-700 border-violet-200',
    local: 'bg-sky-100 text-sky-700 border-sky-200',
    informational: 'bg-amber-100 text-amber-700 border-amber-200',
    conversational: 'bg-pink-100 text-pink-700 border-pink-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[intent] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      {intent || '-'}
    </span>
  );
}

/** Presentational snapshot body — shared by the dashboard tab and the public share page. */
export default function SnapshotView({ snapshot }) {
  const avr = snapshot.ai_visibility_readiness || {};
  const subScores = avr.sub_scores || {};
  const currencySymbol = snapshot.currency_symbol || '€';
  const top3 = (snapshot.keyword_opportunities || []).slice()
    .sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0)).slice(0, 3);

  return (
    <div className="space-y-10">
      {/* AI Visibility Readiness card */}
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 sm:p-8 shadow-sm" data-testid="ai-visibility-card">
        <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center">
          <div className="flex flex-col items-center gap-3 shrink-0">
            <ScoreRing score={avr.overall_score} label="Overall" />
            <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">AI Visibility</div>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Bot className="w-5 h-5 text-violet-600" /> AI Visibility Readiness
            </h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{avr.summary || '-'}</p>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {Object.entries(SUBSCORE_LABEL).map(([k, lbl]) => {
                const v = Number(subScores[k] || 0);
                return (
                  <div key={k} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-slate-100">
                    <span className="text-xs font-medium text-slate-700">{lbl}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, v))}%`, background: v < 45 ? '#dc2626' : v < 65 ? '#f59e0b' : '#10b981' }} />
                      </div>
                      <span className="text-xs font-bold text-slate-900 w-8 text-right">{v}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 opportunity cards */}
      {top3.length > 0 && (
        <div data-testid="top-opportunities">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
            <Crown className="w-5 h-5 text-amber-500" /> Top 3 Opportunities
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {top3.map((o, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">#{i + 1}</div>
                  <div className="px-2 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-bold border border-violet-100">Score {o.opportunity_score}</div>
                </div>
                <h3 className="mt-2 text-base font-bold text-slate-900 leading-snug">{o.keyword}</h3>
                <div className="mt-2 flex items-center gap-2"><IntentBadge intent={o.intent} /></div>
                <div className="mt-4 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between"><span className="text-slate-500">Searches/mo</span><span className="font-semibold text-slate-900">{o.estimated_monthly_searches_range || '-'}</span></div>
                  <div className="flex items-center justify-between"><span className="text-slate-500">Revenue/mo</span><span className="font-semibold text-emerald-700">{o.estimated_monthly_revenue_range || '-'}</span></div>
                </div>
                <p className="mt-3 text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{o.recommended_action}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full keyword opportunities table */}
      <div data-testid="keyword-opportunities-table">
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
          <TrendingUp className="w-5 h-5 text-violet-600" /> Keyword Opportunities
          <span className="ml-2 text-xs font-normal text-slate-500">({snapshot.keyword_opportunities?.length || 0} AI-estimated)</span>
        </h2>
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Keyword</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Intent</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Searches/mo</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Revenue/mo ({currencySymbol})</th>
                <th className="text-right px-4 py-3 font-semibold text-slate-700">Score</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-700">Recommended action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {(snapshot.keyword_opportunities || []).map((o, i) => (
                <tr key={i} className="hover:bg-slate-50/60 transition">
                  <td className="px-4 py-3 font-medium text-slate-900">{o.keyword}</td>
                  <td className="px-4 py-3"><IntentBadge intent={o.intent} /></td>
                  <td className="px-4 py-3 text-right text-slate-700">{o.estimated_monthly_searches_range || '-'}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{o.estimated_monthly_revenue_range || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-50 text-violet-700 text-xs font-bold border border-violet-100">{o.opportunity_score ?? '-'}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs leading-relaxed max-w-md">{o.recommended_action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Opportunities Detected */}
      {snapshot.opportunities_detected?.length > 0 && (
        <div data-testid="opportunities-detected">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
            <BadgeAlert className="w-5 h-5 text-amber-500" /> Opportunities Detected
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {snapshot.opportunities_detected.map((op, i) => {
              const sev = (op.severity || 'medium').toLowerCase();
              const sevCls = sev === 'high' ? 'bg-rose-50 border-rose-200 text-rose-700'
                : sev === 'low' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-amber-50 border-amber-200 text-amber-700';
              return (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-bold text-slate-900 leading-snug">{op.title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${sevCls}`}>{sev}</span>
                      </div>
                      <p className="mt-2 text-xs text-slate-600 leading-relaxed">{op.detail}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommended actions */}
      {snapshot.recommended_actions?.length > 0 && (
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-violet-50/30 p-6 sm:p-8" data-testid="recommended-actions">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
            <Target className="w-5 h-5 text-violet-600" /> Recommended Actions
          </h2>
          <ul className="space-y-3">
            {snapshot.recommended_actions.map((a, i) => (
              <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-slate-100">
                <span className="shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-sm text-slate-700 leading-relaxed">{a}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Disclaimer */}
      <div className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
        <strong className="font-semibold text-slate-800">Disclaimer:</strong> These insights are AI-estimated opportunity indicators and not real-time rankings or live AI engine visibility data.
      </div>
    </div>
  );
}
